import type { z } from 'zod';

/**
 * Parse `raw` against the provided Zod schema. On failure, log a
 * console warning (dev only) and fall back to a type-cast of the raw
 * response so the UI keeps rendering with whatever the platform sent.
 *
 * Why: the gen-3 schemas validate the *intended* shape, but the platform
 * occasionally returns null fields or extra variants we haven't
 * enumerated. Crashing the page on a single missing field hides the
 * working content (criteria, study period) for no benefit. Soft-fail
 * with a warning lets us iterate on the schema while keeping the
 * cohort detail page usable.
 */
export const parseOrFallback = <T>(schema: z.ZodType<T>, raw: unknown, context: string): T => {
  const result = schema.safeParse(raw);
  if (result.success) {
    return result.data;
  }
  if (import.meta.env?.DEV) {
    console.warn(
      `[gen-3] zod schema mismatch in ${context} — falling back to raw response`,
      result.error.issues
    );
  }
  return raw as T;
};
